import { useEffect, useMemo, useState } from "react";
import { Link2, Loader2, X } from "lucide-react";
import { createPortal } from "react-dom";

export default function ConnectNodeDialog({
    open,
    sourceNode,
    availableNodes = [],
    existingTransitions = [],
    onClose,
    onConnect,
}) {
    const [targetNodeId, setTargetNodeId] = useState("");
    const [actionGroup, setActionGroup] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const connectedIds = useMemo(
        () => new Set(existingTransitions.map((transition) => String(transition.to_node_id))),
        [existingTransitions]
    );
    const candidates = useMemo(
        () => availableNodes.filter((node) =>
            String(node.id) !== String(sourceNode?.id) &&
            !connectedIds.has(String(node.id))
        ),
        [availableNodes, connectedIds, sourceNode?.id]
    );

    useEffect(() => {
        if (!open) return;
        setTargetNodeId("");
        setActionGroup("");
        setSubmitting(false);
    }, [open, sourceNode?.id]);

    if (!open) return null;

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!targetNodeId || submitting) return;

        setSubmitting(true);
        try {
            await onConnect?.({
                from_node_id: sourceNode.id,
                to_node_id: targetNodeId,
                action_group_name: actionGroup.trim() || null,
                metadata: {},
            });
        } finally {
            setSubmitting(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#071321]/45 p-4 backdrop-blur-sm" onMouseDown={(event) => event.target === event.currentTarget && onClose?.()}>
            <div role="dialog" aria-modal="true" aria-labelledby="connect-node-title" className="w-full max-w-lg rounded-3xl border border-white/60 bg-white shadow-[0_30px_90px_rgba(15,43,92,0.24)]">
                <header className="flex items-center justify-between border-b border-[#e4edf5] px-6 py-5">
                    <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#e8f6fd] text-[#147db5]"><Link2 className="h-5 w-5" /></span>
                        <div><h2 id="connect-node-title" className="text-lg font-bold text-[#102b5c]">Connect an existing action</h2><p className="mt-0.5 text-sm text-[#7185a3]">Continue after “{sourceNode?.name}”.</p></div>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close connection editor" className="flex h-9 w-9 items-center justify-center rounded-xl text-[#7185a3] hover:bg-[#f1f6fb]"><X className="h-[18px] w-[18px]" /></button>
                </header>

                <form onSubmit={handleSubmit} className="space-y-5 p-6">
                    <div>
                        <label className="mb-1.5 block text-xs font-bold text-[#294d63]">Target action</label>
                        <select value={targetNodeId} onChange={(event) => setTargetNodeId(event.target.value)} required className="h-11 w-full rounded-xl border border-[#cdddea] bg-white px-3.5 text-sm text-[#173552] outline-none focus:border-[#49a9d8] focus:ring-2 focus:ring-[#dff3fc]">
                            <option value="">{candidates.length ? "Choose an action..." : "No available actions"}</option>
                            {candidates.map((node) => <option key={node.id} value={node.id}>{node.stage_name ? `${node.stage_name} — ` : ""}{node.name} ({node.node_type})</option>)}
                        </select>
                        <p className="mt-1.5 text-[11px] leading-5 text-[#7b8da9]">You can connect to an action in this stage or a later stage.</p>
                    </div>

                    <div>
                        <label className="mb-1.5 block text-xs font-bold text-[#294d63]">Path label</label>
                        <input value={actionGroup} onChange={(event) => setActionGroup(event.target.value)} placeholder="e.g. Qualified, Continue, No answer" maxLength={100} className="h-11 w-full rounded-xl border border-[#cdddea] px-3.5 text-sm text-[#173552] outline-none placeholder:text-[#9bacc0] focus:border-[#49a9d8] focus:ring-2 focus:ring-[#dff3fc]" />
                        <p className="mt-1.5 text-[11px] leading-5 text-[#7b8da9]">For decision actions, use a clear outcome that the salesperson can select.</p>
                    </div>

                    <div className="flex justify-end gap-2.5 border-t border-[#edf2f7] pt-5">
                        <button type="button" onClick={onClose} className="h-10 rounded-xl border border-[#d5e4f2] px-4 text-sm font-bold text-[#607b9f] hover:bg-[#f5f9fd]">Cancel</button>
                        <button type="submit" disabled={submitting || !targetNodeId} className="inline-flex h-10 min-w-[116px] items-center justify-center gap-2 rounded-xl bg-[#147db5] px-4 text-sm font-bold text-white hover:bg-[#0f6f9f] disabled:cursor-not-allowed disabled:opacity-50">{submitting && <Loader2 className="h-4 w-4 animate-spin" />} Connect</button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
}
